import styled, { css } from 'styled-components'
import { noop } from 'lodash'
import { isNil } from 'ramda'
import { useTranslation } from 'react-i18next'
import { breakpoints, colors, fontWeights } from '@/styles'
import { ValueText } from '@/features/Application/elements'
import Tooltip from '@/components/custom-tooltip'
import InfoCircleIcon from '@/features/Application/icons/info-circle.svg?react'
import Skeleton from '@/components/skeleton'

type rateType = 'default' | 'poor' | 'average' | 'very_good' | 'excellent'

const defaultStyle = css`
  color: ${colors.grey32};
`

const poor = css`
  color: ${colors.red40};
`

const average = css`
  color: ${colors.yellow48};
`

const veryGood = css`
  color: ${colors.veryGood};
`

const excellent = css`
  color: ${colors.green48};
`

const getTypeStyles = (props: { rate: rateType }) => {
  if (props.rate === 'poor') return poor
  if (props.rate === 'average') return average
  if (props.rate === 'very_good') return veryGood
  if (props.rate === 'excellent') return excellent

  return defaultStyle
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0 12px 24px 12px;
  }
`
const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`

const Title = styled.p`
  font-weight: ${fontWeights.medium};
  font-size: 20px;
  line-height: 28px;
  color: ${colors.grey16};
  margin: 0 10px 0 0;
`

const InfoButton = styled.button`
  outline: none;
  border: none;
  background: transparent;
  padding: 0px;
  cursor: pointer;

  svg {
    pointer-events: none;
  }
`

const StyledIcon = styled(InfoCircleIcon)`
  cursor: pointer;
`

const ScoreWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-end;
`

const ScoreText = styled.p<{ rate: rateType }>`
  font-size: 24px;
  line-height: 32px;
  font-weight: ${fontWeights.bold};
  margin: 0;
  ${props => getTypeStyles(props)};
`
const AnalyzingText = styled.p`
  font-size: 24px;
  line-height: 32px;
  font-weight: ${fontWeights.bold};
  margin: 0;
`
const TotalText = styled.p`
  font-size: 16px;
  line-height: 20px;
  font-weight: ${fontWeights.medium};
  margin: 0 0 5.5px 5px;
`
type ScoreComponentProps = {
  score?: number
  maxScore?: number
  incompleteScore?: boolean
  rate?: rateType
  isLocked?: boolean
  isLoading?: boolean
}

const ScoreComponent: React.FC<ScoreComponentProps> = ({
  isLocked = false,
  isLoading = false,
  rate = 'default',
  score = null,
  incompleteScore = false,
  maxScore
}) => {
  const { t } = useTranslation()
  const randomWord = '*****'
  if (isLocked) return <ValueText isLocked={isLocked}>{randomWord}</ValueText>
  if (isLoading) return <Skeleton width={70} height={30} />
  return incompleteScore ? (
    <Tooltip
      text={t(
        'l.application.view.analyzing_score.tooltip',
        `* This score is subject to change as we're still analyzing this applicant's information. Please check back in 1-3 business days.`
      )}
      placement="top"
      openOnMobilePress
      offsetLeft={0}
      offsetRight={0}
    >
      <ScoreWrapper>
        <ScoreText rate={rate}>{score}</ScoreText>
        {incompleteScore && <AnalyzingText>*</AnalyzingText>}
        <TotalText>{`/ ${maxScore}`}</TotalText>
      </ScoreWrapper>
    </Tooltip>
  ) : (
    <ScoreWrapper>
      <ScoreText rate={rate}>{score}</ScoreText>
      <TotalText>{`/ ${maxScore}`}</TotalText>
    </ScoreWrapper>
  )
}

type SectionTitleProps = {
  title: string
  showInfo?: boolean
  handleInfoClick?: () => void
  score?: number
  maxScore?: number
  incompleteScore?: boolean
  rate?: rateType
  isLocked?: boolean
  clickId?: string
  isLoading?: boolean
}

const SectionTitle: React.FC<SectionTitleProps> = ({
  title = '',
  showInfo = false,
  handleInfoClick = noop,
  score = null,
  maxScore,
  incompleteScore = false,
  rate = 'default',
  isLocked,
  clickId = '',
  isLoading = false
}) => {
  return (
    <Wrapper>
      <TitleWrapper>
        <Title>{title}</Title>
        {showInfo && (
          <InfoButton id={clickId} onClick={handleInfoClick}>
            <StyledIcon />
          </InfoButton>
        )}
      </TitleWrapper>
      {!isNil(score) && (
        <ScoreComponent
          isLocked={isLocked}
          isLoading={isLoading}
          rate={rate}
          score={score}
          incompleteScore={incompleteScore}
          maxScore={maxScore}
        />
      )}
    </Wrapper>
  )
}

export default SectionTitle
